import type {
  ApiResponse,
  PaginatedResponse,
  User,
  Subscription,
  MemberListItem,
  MemberDetail,
  TrainingSession,
  Membership
} from "./types";
import type {
  MeasurementBooking,
  CreateMeasurementBookingInput,
} from "./measurement-bookings";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE ?? "";

type RequestOptions = RequestInit & {
  skipCsrf?: boolean;
};

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length !== 2) return null;

  return decodeURIComponent(parts.pop()?.split(";").shift() ?? "");
}

async function csrfCookie() {
  await fetch(`${API_BASE}/sanctum/csrf-cookie`, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/json",
    },
  });
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { skipCsrf, ...fetchOptions } = options;

  const method = fetchOptions.method?.toUpperCase() ?? "GET";

  const needsCsrf = !skipCsrf && !["GET", "HEAD", "OPTIONS"].includes(method);

  if (needsCsrf) {
    await csrfCookie();
  }

  const xsrfToken = getCookie("XSRF-TOKEN");

  const response = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(xsrfToken ? { "X-XSRF-TOKEN": xsrfToken } : {}),
      ...(fetchOptions.headers ?? {}),
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw data ?? {
      success: false,
      message: "Došlo je do pogreške.",
    };
  }

  return data;
}


export async function getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
  return apiFetch<ApiResponse<{ user: User }>>("/api/auth/me", {
    skipCsrf: true,
  });
}

export async function login(input: {
  email: string;
  password: string;
}): Promise<ApiResponse<{ user: User }>> {
  return apiFetch<ApiResponse<{ user: User }>>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function logout(): Promise<ApiResponse<null>> {
  return apiFetch<ApiResponse<null>>("/api/auth/logout", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function getSubscriptions(): Promise<
  ApiResponse<{ items: Subscription[] }>
> {
  return apiFetch<ApiResponse<{ items: Subscription[] }>>("/api/subscriptions", {
    skipCsrf: true,
  });
}

export async function getAdminMembers(params?: {
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<PaginatedResponse<MemberListItem>> {
  const searchParams = new URLSearchParams();

  if (params?.search) searchParams.set("search", params.search);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.per_page) searchParams.set("per_page", String(params.per_page));

  const query = searchParams.toString();

  return apiFetch<PaginatedResponse<MemberListItem>>(
    `/api/admin/members${query ? `?${query}` : ""}`,
    { skipCsrf: true },
  );
}

export async function getAdminMember(
  id: number,
): Promise<ApiResponse<{ member: MemberDetail }>> {
  return apiFetch<ApiResponse<{ member: MemberDetail }>>(
    `/api/admin/members/${id}`,
    { skipCsrf: true },
  );
}

export async function getArchivedMembers(params?: {
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<PaginatedResponse<MemberListItem>> {
  const searchParams = new URLSearchParams();

  if (params?.search) searchParams.set("search", params.search);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.per_page) searchParams.set("per_page", String(params.per_page));

  const query = searchParams.toString();

  return apiFetch<PaginatedResponse<MemberListItem>>(
    `/api/admin/archive${query ? `?${query}` : ""}`,
    { skipCsrf: true },
  );
}

export async function getTrainingSessions(params?: {
  from?: string;
  to?: string;
}): Promise<ApiResponse<{ items: TrainingSession[] }>> {
  const searchParams = new URLSearchParams();

  if (params?.from) searchParams.set("from", params.from);
  if (params?.to) searchParams.set("to", params.to);

  const query = searchParams.toString();

  return apiFetch<ApiResponse<{ items: TrainingSession[] }>>(
    `/api/admin/training-sessions${query ? `?${query}` : ""}`,
    { skipCsrf: true },
  );
}

export type RegisterInput = {
  email: string;
  password: string;
};

export async function register(
  input: RegisterInput,
): Promise<ApiResponse<{ email: string; user: User }>> {
  return apiFetch<ApiResponse<{ email: string; user: User }>>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function resendVerificationEmail(input: {
  email: string;
}): Promise<ApiResponse<{ email: string }>> {
  return apiFetch<ApiResponse<{ email: string }>>(
    "/api/auth/email/verification-notification",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function forgotPassword(input: {
  email: string;
}): Promise<ApiResponse<{ email: string }>> {
  return apiFetch<ApiResponse<{ email: string }>>(
    "/api/auth/forgot-password",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function resetPassword(input: {
  email: string;
  token: string;
  password: string;
}): Promise<ApiResponse<null>> {
  return apiFetch<ApiResponse<null>>("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function changePassword(input: {
  current_password: string;
  password: string;
}): Promise<ApiResponse<null>> {
  return apiFetch<ApiResponse<null>>("/api/profile/password", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function startEmailChange(input: {
  new_email: string;
}): Promise<
  ApiResponse<{
    current_email: string;
    new_email: string;
  }>
> {
  return apiFetch<
    ApiResponse<{
      current_email: string;
      new_email: string;
    }>
  >("/api/profile/email-change", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function resendEmailChange(input: {
  new_email: string;
}): Promise<ApiResponse<{ new_email: string }>> {
  return apiFetch<ApiResponse<{ new_email: string }>>(
    "/api/profile/email-change/resend",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function archiveMember(
  memberId: number,
): Promise<
  ApiResponse<{
    member_id: number;
    archived: boolean;
    archived_at: string;
  }>
> {
  return apiFetch<
    ApiResponse<{
      member_id: number;
      archived: boolean;
      archived_at: string;
    }>
  >(`/api/admin/members/${memberId}/archive`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export type CreateAdminMemberInput = {
  full_name: string;
  birth_date?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  gender?: "male" | "female" | null;
  member_from: string;
  expires_at: string;
  subscription_id: number;
  group_training_scheduled_at?: string | null;
};

export async function createAdminMember(
  input: CreateAdminMemberInput,
): Promise<ApiResponse<{ member: MemberDetail }>> {
  return apiFetch<ApiResponse<{ member: MemberDetail }>>("/api/admin/members", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export type UpdateAdminMemberInput = {
  full_name: string;
  birth_date?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  gender?: "male" | "female" | null;
};

export type CreateMembershipInput = {
  subscription_id: number;
  member_from: string;
  expires_at: string;
};

export async function updateAdminMember(
  id: number,
  input: UpdateAdminMemberInput,
): Promise<ApiResponse<{ member: MemberDetail }>> {
  return apiFetch<ApiResponse<{ member: MemberDetail }>>(
    `/api/admin/members/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export async function createMembership(
  memberId: number,
  input: CreateMembershipInput,
): Promise<ApiResponse<{ membership: Membership }>> {
  return apiFetch<ApiResponse<{ membership: Membership }>>(
    `/api/admin/members/${memberId}/memberships`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function restoreMember(
  memberId: number,
): Promise<
  ApiResponse<{
    member_id: number;
    archived: boolean;
    archived_at: null;
  }>
> {
  return apiFetch<
    ApiResponse<{
      member_id: number;
      archived: boolean;
      archived_at: null;
    }>
  >(`/api/admin/members/${memberId}/restore`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function addMemberToTrainingSession(input: {
  scheduled_at: string;
  member_info_id: number;
}): Promise<ApiResponse<{ session: TrainingSession }>> {
  return apiFetch<ApiResponse<{ session: TrainingSession }>>(
    "/api/admin/training-sessions",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function removeMemberFromTrainingSession(
  sessionId: number,
  memberId: number,
): Promise<
  ApiResponse<{
    session_id: number;
    member_info_id: number;
  }>
> {
  return apiFetch<
    ApiResponse<{
      session_id: number;
      member_info_id: number;
    }>
  >(`/api/admin/training-sessions/${sessionId}/attendees/${memberId}/remove`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function confirmEmailChange(
  token: string,
): Promise<ApiResponse<{ email: string }>> {
  return apiFetch<ApiResponse<{ email: string }>>(
    `/api/profile/email-change/confirm/${encodeURIComponent(token)}`,
    {
      skipCsrf: true,
    },
  );
}

export async function getMeasurementAvailableSlots(
  date: string,
): Promise<ApiResponse<{ items: string[] }>> {
  return apiFetch<ApiResponse<{ items: string[] }>>(
    `/api/measurement-bookings/available-slots?date=${encodeURIComponent(date)}`,
    { skipCsrf: true },
  );
}

export async function createMeasurementBooking(
  input: CreateMeasurementBookingInput,
): Promise<ApiResponse<{ booking: MeasurementBooking }>> {
  return apiFetch<ApiResponse<{ booking: MeasurementBooking }>>(
    "/api/measurement-bookings",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function getAdminMeasurementBookings(params?: {
  status?: MeasurementBooking["status"];
}): Promise<ApiResponse<{ items: MeasurementBooking[] }>> {
  const query = params?.status ? `?status=${encodeURIComponent(params.status)}` : "";

  return apiFetch<ApiResponse<{ items: MeasurementBooking[] }>>(
    `/api/admin/measurement-bookings${query}`,
    { skipCsrf: true },
  );
}

export async function confirmMeasurementBooking(
  id: number,
): Promise<ApiResponse<{ booking: MeasurementBooking }>> {
  return apiFetch<ApiResponse<{ booking: MeasurementBooking }>>(
    `/api/admin/measurement-bookings/${id}/confirm`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export async function declineMeasurementBooking(
  id: number,
): Promise<ApiResponse<{ booking: MeasurementBooking }>> {
  return apiFetch<ApiResponse<{ booking: MeasurementBooking }>>(
    `/api/admin/measurement-bookings/${id}/decline`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}
